import { blue, brand, common, gray, green, orange, purple, red } from "./colors";
import { defaultThemeMode, isSupportedThemeMode, themeModes } from "./modes";

const customScales = {
  gray,
  brand,
  common,
  blue,
  green,
  orange,
  red,
  purple,
};

const lightPalette = {
  focusRing: "rgba(129, 66, 252, 0.18)",
  errorRing: "rgba(239, 68, 68, 0.18)",
  primary: {
    light: purple[300],
    main: purple[500],
    dark: purple[700],
    contrastText: common.white,
  },
  secondary: {
    light: purple[100],
    main: purple[600],
    dark: purple[800],
    contrastText: common.white,
  },
  success: {
    light: green[100],
    main: green[500],
    dark: green[800],
    contrastText: common.white,
  },
  warning: {
    light: orange[100],
    main: orange[500],
    dark: orange[700],
    contrastText: gray[900],
  },
  error: {
    light: red[100],
    main: red[500],
    dark: red[800],
    contrastText: common.white,
  },
  info: {
    light: purple[100],
    main: purple[500],
    dark: purple[700],
    contrastText: common.white,
  },
  divider: "rgba(82, 82, 91, 0.18)",
  background: {
    default: gray[50],
    paper: common.white,
  },
  text: {
    primary: gray[900],
    secondary: gray[600],
    disabled: gray[400],
  },
  action: {
    hover: "rgba(129, 66, 252, 0.06)",
    selected: "rgba(129, 66, 252, 0.12)",
    disabled: gray[400],
    disabledBackground: gray[100],
  },
  surface: {
    default: common.white,
    muted: gray[50],
    subtle: gray[100],
    inverse: gray[900],
  },
  border: {
    default: gray[200],
    muted: gray[100],
    strong: gray[300],
    focus: purple[500],
    error: red[500],
  },
  feedback: {
    success: {
      surface: green[50],
      border: green[200],
      text: green[700],
      accent: green[500],
    },
    warning: {
      surface: orange[50],
      border: orange[200],
      text: orange[700],
      accent: orange[500],
    },
    error: {
      surface: red[50],
      border: red[200],
      text: red[700],
      accent: red[500],
    },
    info: {
      surface: purple[50],
      border: purple[200],
      text: purple[700],
      accent: purple[500],
    },
    neutral: {
      surface: gray[50],
      border: gray[200],
      text: gray[700],
      accent: gray[500],
    },
  },
};

const darkPalette = {
  focusRing: "rgba(169, 134, 255, 0.26)",
  errorRing: "rgba(248, 113, 113, 0.22)",
  primary: {
    light: purple[300],
    main: purple[400],
    dark: purple[600],
    contrastText: common.white,
  },
  secondary: {
    light: purple[200],
    main: purple[400],
    dark: purple[700],
    contrastText: common.white,
  },
  success: {
    light: green[300],
    main: green[400],
    dark: green[700],
    contrastText: gray[900],
  },
  warning: {
    light: orange[300],
    main: orange[400],
    dark: orange[700],
    contrastText: gray[900],
  },
  error: {
    light: red[300],
    main: red[400],
    dark: red[700],
    contrastText: gray[900],
  },
  info: {
    light: blue[300],
    main: purple[400],
    dark: purple[700],
    contrastText: common.white,
  },
  divider: "rgba(212, 212, 216, 0.14)",
  background: {
    default: gray[900],
    paper: gray[800],
  },
  text: {
    primary: gray[50],
    secondary: gray[300],
    disabled: gray[500],
  },
  action: {
    hover: "rgba(169, 134, 255, 0.1)",
    selected: "rgba(169, 134, 255, 0.16)",
    disabled: gray[500],
    disabledBackground: gray[800],
  },
  surface: {
    default: gray[900],
    muted: gray[800],
    subtle: gray[800],
    inverse: common.white,
  },
  border: {
    default: gray[700],
    muted: gray[800],
    strong: gray[600],
    focus: purple[400],
    error: red[400],
  },
  feedback: {
    success: {
      surface: "rgba(20, 83, 45, 0.28)",
      border: green[700],
      text: green[200],
      accent: green[400],
    },
    warning: {
      surface: "rgba(124, 45, 18, 0.28)",
      border: orange[700],
      text: orange[200],
      accent: orange[400],
    },
    error: {
      surface: "rgba(127, 29, 29, 0.28)",
      border: red[700],
      text: red[200],
      accent: red[400],
    },
    info: {
      surface: "rgba(48, 13, 104, 0.36)",
      border: purple[700],
      text: purple[100],
      accent: purple[400],
    },
    neutral: {
      surface: gray[800],
      border: gray[700],
      text: gray[200],
      accent: gray[400],
    },
  },
};

const palettesByMode = {
  [themeModes.light]: lightPalette,
  [themeModes.dark]: darkPalette,
};

export function createPalette(mode = defaultThemeMode) {
  const resolvedMode = isSupportedThemeMode(mode) ? mode : defaultThemeMode;
  const modePalette = palettesByMode[resolvedMode] || palettesByMode[defaultThemeMode];

  return {
    mode: resolvedMode,
    primary: modePalette.primary,
    secondary: modePalette.secondary,
    success: modePalette.success,
    warning: modePalette.warning,
    error: modePalette.error,
    info: modePalette.info,
    grey: gray,
    divider: modePalette.divider,
    background: modePalette.background,
    text: modePalette.text,
    action: modePalette.action,
    surface: modePalette.surface,
    border: modePalette.border,
    feedback: modePalette.feedback,
    focusRing: modePalette.focusRing,
    errorRing: modePalette.errorRing,
    custom: customScales,
  };
}

export const palette = createPalette(defaultThemeMode);
